// @flow @format
import { type Store, applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import { createEpicMiddleware } from 'redux-observable';
import { createTransform, persistStore, persistReducer, REHYDRATE } from 'redux-persist';
import { routerMiddleware } from 'connected-react-router';
import * as L from 'partial.lenses';
import storageSession from 'redux-persist/lib/storage/session';
import F from 'lodash/fp';

import { createBrowserHistory } from 'history';

import type { Action, Dispatch, StoreState } from './types';
import { fetchEntitiesSuccess } from './reducer/entities';
import createRootReducer from './reducer';
import epics from './epics';

const epicMiddleware = createEpicMiddleware();

declare var __GA_ID__: string;

const stateSanitizer = (state: StoreState): StoreState =>
  L.modify(['entities', 'qdls', 'byId', L.values], (_, k) => `qdl for ${k}`, state);

const actionSanitizer = (action: Action): Action => {
  if (action.type === fetchEntitiesSuccess.type)
    return L.modify(['payload', 'entities', 'qdls', L.values], (_, k) => `qdl for ${k}`, action);
  // $FlowIgnore
  if (action.type === REHYDRATE) return L.modify('payload', stateSanitizer, action);
  return action;
};

// $FlowFixMe
const composeEnhancers = composeWithDevTools({
  actionSanitizer,
  stateSanitizer,
});

export const history = createBrowserHistory();

history.listen(location => {
  if (!window.gtag) return; // gtag not found

  window.gtag('config', __GA_ID__, { page_path: location.pathname });
  window.gtag('event', 'page_view', { send_to: __GA_ID__ });
});

const rootReducer = createRootReducer(history);

const enhancer = composeEnhancers(
  applyMiddleware(
    routerMiddleware(history), // for dispatching history actio
    epicMiddleware
  )
);

// We set all loading props to false before persist, in case of user reload/navigate while loading query is still
// haven't returned
// We also strip fetched at props
const stripLoadingTransform = createTransform(
  inboundState => F.pipe(L.set(L.query('loading'), false), L.remove(L.query('fetchedAt')))(inboundState),
  outboundState => outboundState,
  { blacklist: [] }
);

const persistConfig = {
  key: 'root',
  storage: storageSession,
  transforms: [stripLoadingTransform],
  whitelist: ['profile', 'config', 'auth', 'term', 'entities', 'progresses'],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store: Store<StoreState, Action, Dispatch> = createStore(persistedReducer, enhancer);
const persistor = persistStore(store);

epicMiddleware.run(epics);

export { store, persistor };
